import { DIAS_MAX_RESERVA } from '../shared/constants.js';

/**
 * Pure domain rules for reservations.
 * No DOM, no fetch, no state — safe to import in Node (api/) and browser (js/).
 */

export const ANTICIPACION_HORAS = 2;

export const MOMENTOS = ["Desayuno", "Almuerzo", "Merienda", "Cena", "Trasnoche"];

export const RESERVA_SLOTS = {
    "Desayuno":  ["09:30", "10:00", "10:30"],
    "Almuerzo":  ["12:00", "12:30", "13:00", "13:30"],
    "Merienda":  ["17:00", "17:30"],
    "Cena":      ["21:00", "21:30", "22:00"],
    "Trasnoche": ["00:30"],
};

/**
 * Momentos reservables según el día, siguiendo los horarios de atención:
 *  - Lunes (1) y Martes (2): solo tarde/noche (abre 17hs, mañanas cerrado) →
 *    Merienda + Cena, sin Desayuno/Almuerzo.
 *  - Miércoles/Jueves/Domingo (3,4,0): mañana (9-13) + tarde/noche (17-00),
 *    sin Trasnoche.
 *  - Viernes (5) y Sábado (6): horario extendido → suman Trasnoche.
 *
 * @param {Date} date
 * @returns {string[]}
 */
export function getAvailableMomentos(date) {
    const day = date.getDay(); // 0=Dom … 6=Sáb
    const eveningOnly = day === 1 || day === 2;   // Lun/Mar: abre 17hs
    const extended = day === 5 || day === 6;      // Vie/Sáb: +Trasnoche
    return MOMENTOS.filter(m => {
        if (eveningOnly) return m === "Merienda" || m === "Cena";
        if (m === "Trasnoche") return extended;
        return true;
    });
}

/**
 * Returns the time slots for a given fecha+momento that still respect
 * the minimum advance booking window (ANTICIPACION_HORAS).
 *
 * @param {string} fecha  — ISO date string, e.g. "2026-06-15"
 * @param {string} momento — e.g. "Cena"
 * @param {Date}   [now]  — injectable for testing (defaults to new Date())
 * @returns {string[]}
 */
export function getValidTimeSlots(fecha, momento, now = new Date()) {
    const allSlots = RESERVA_SLOTS[momento] || [];
    return allSlots.filter(hora => {
        const [h, m] = hora.split(':');
        const slotDate = new Date(`${fecha}T${h}:${m}:00`);
        return (slotDate - now) / (1000 * 60 * 60) >= ANTICIPACION_HORAS;
    });
}

/**
 * Lista los próximos `days` días consecutivos a partir de hoy (incluido hoy)
 * para el alta manual de staff ("Nueva reserva"). A diferencia de
 * `generateDateChips`, NO filtra por disponibilidad ni por la anticipación de
 * 2h: staff es "libre" y todos los días abren — sólo enumera las fechas con una
 * etiqueta legible (día de la semana + número + mes corto).
 *
 * Deriva weekday/label desde un Date a mediodía local (`${iso}T12:00:00`) para
 * evitar el off-by-one por zona horaria, mismo criterio que `generateDateChips`.
 *
 * @param {Date}   [now]  — inyectable para tests (default `new Date()`)
 * @param {number} [days] — cantidad de días a listar (default DIAS_MAX_RESERVA)
 * @returns {{ iso: string, label: string }[]}
 */
export function getUpcomingDates(now = new Date(), days = DIAS_MAX_RESERVA) {
    const dayNames   = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    const dates = [];

    for (let i = 0; i < days; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const noon = new Date(`${iso}T12:00:00`);
        dates.push({
            iso,
            label: `${dayNames[noon.getDay()]} ${noon.getDate()} de ${monthNames[noon.getMonth()]}`,
        });
    }

    return dates;
}

/**
 * Generates the date chips shown in Step 2 of the reservation wizard.
 * Emits every bookable date inside the booking window (DIAS_MAX_RESERVA):
 * a day shows up only if it still has a slot that respects ANTICIPACION_HORAS.
 * There is no cap on how many chips come out — the strip scrolls.
 *
 * @param {Date}   [now]     — injectable for testing (defaults to new Date())
 * @param {number} [maxDays] — calendar window to scan (default DIAS_MAX_RESERVA)
 * @returns {{ iso: string, dayName: string, label: string }[]}
 */
export function generateDateChips(now = new Date(), maxDays = DIAS_MAX_RESERVA) {
    const dates = [];
    const dayNames   = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
    const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

    for (let i = 0; i < maxDays; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);

        // Ya no se saltea ningún día fijo: todos abren. Un día aparece solo si
        // tiene algún momento disponible (getAvailableMomentos) con un slot que
        // respeta la anticipación mínima.
        // Fecha LOCAL, no UTC. Con `toISOString()` el chip se iba un día para
        // adelante después de las 21:00 en Argentina (UTC-3): el label decía
        // "Hoy, 1 de Sep" y el `iso` que se manda al servidor era el 2. El
        // label ya se armaba con las partes locales (`d.getDate()`), así que
        // los dos lados discrepaban y el cliente reservaba para otro día.
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        const hasValidSlot = getAvailableMomentos(d).some(momento =>
            (RESERVA_SLOTS[momento] || []).some(hora => {
                const [h, m] = hora.split(':');
                const slotDate = new Date(`${iso}T${h}:${m}:00`);
                return (slotDate - now) / (1000 * 60 * 60) >= ANTICIPACION_HORAS;
            })
        );

        if (hasValidSlot) {
            dates.push({
                iso,
                dayName: i === 0 ? "Hoy" : (i === 1 ? "Mañana" : dayNames[d.getDay()]),
                label: `${d.getDate()} de ${monthNames[d.getMonth()]}`,
            });
        }
    }

    return dates;
}
